import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuRoute } from 'react-icons/lu';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import MedioViaje from '../ui/MedioViaje';

// Ruta que propuso una empresa (23-oct-2026): el admin la confirma (cada pedido queda agendado ese día,
// con la hora estimada por el recorrido, y le avisa al técnico y a la empresa) o la descarta.
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const diaLargo = iso => { const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number); return `${DIAS[new Date(y, m - 1, d).getDay()]} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`; };

export default function ConfirmarRutaSheet({ ruta, tecnicos = [], onCerrar, onListo }) {
    // ruta: { clienteId, clienteNombre, fecha, pedidos: [...] en orden }
    const lucas = tecnicos.find(t => /lucas/i.test(t.nombre || '')) || tecnicos[0];
    const [tecnicoId, setTecnicoId] = useState(lucas?.id || null);
    const [descartando, setDescartando] = useState(false);
    const [motivo, setMotivo] = useState('');
    const [guardando, setGuardando] = useState(false);
    const [medio, setMedio] = useState('CAMIONETA');   // con la camioneta de la empresa, salvo que vayas en colectivo
    const equipos = ruta.pedidos.reduce((s, p) => s + Math.max((p.equipos || []).length, 1), 0);

    const llamar = async (url, cuerpo, ok) => {
        setGuardando(true);
        try { await api.post(url, cuerpo); toast.success(ok); onListo && onListo(); }
        catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); }
        finally { setGuardando(false); }
    };
    const confirmar = () => llamar('/pedidos-empresa/ruta/confirmar', { clienteId: ruta.clienteId, fecha: ruta.fecha, tecnicoId, medio },
        `Ruta agendada: ${ruta.pedidos.length} visita${ruta.pedidos.length !== 1 ? 's' : ''} el ${diaLargo(ruta.fecha)}`);
    const descartar = () => llamar('/pedidos-empresa/ruta/descartar', { clienteId: ruta.clienteId, fecha: ruta.fecha, motivo },
        'Ruta descartada: le avisamos a la empresa');

    const pie = descartando ? (
        <div className="flex gap-2">
            <button type="button" onClick={() => setDescartando(false)} className="h-12 px-4 rounded-xl bg-chip text-secondary text-label font-black">Volver</button>
            <button type="button" onClick={descartar} disabled={guardando}
                className="flex-1 h-12 rounded-xl bg-brand-red text-white text-label font-black uppercase active:scale-95 disabled:opacity-40">Descartar la ruta</button>
        </div>
    ) : (
        <div className="flex gap-2">
            <button type="button" onClick={() => setDescartando(true)} className="h-12 px-4 rounded-xl bg-chip text-secondary text-label font-black">Descartar</button>
            <button type="button" onClick={confirmar} disabled={guardando || !tecnicoId}
                className="flex-1 h-12 rounded-xl bg-brand-red text-white text-label font-black uppercase inline-flex items-center justify-center gap-2 active:scale-95 disabled:opacity-40">
                <LuRoute size={16} />{guardando ? 'Agendando…' : 'Confirmar y agendar'}
            </button>
        </div>
    );

    return (
        <ModalShell titulo={`Ruta del ${diaLargo(ruta.fecha)}`} subtitulo={`La armó ${ruta.clienteNombre || 'la empresa'} · ${ruta.pedidos.length} pedido${ruta.pedidos.length !== 1 ? 's' : ''} · ${equipos} equipo${equipos !== 1 ? 's' : ''}`}
            onCerrar={onCerrar} pie={pie}>
            <div className="space-y-4">
                <ol className="rounded-2xl bg-panel divide-y divide-black/[0.06] dark:divide-white/[0.06] overflow-hidden">
                    {ruta.pedidos.map((p, i) => (
                        <li key={p.id} className="flex items-center gap-3 px-3 py-2.5">
                            <span className="w-7 h-7 shrink-0 rounded-full bg-brand-red text-white inline-flex items-center justify-center text-label font-black">{i + 1}</span>
                            <span className="flex-1 min-w-0">
                                <span className="block text-body font-black text-ink truncate">{[p.codigo, p.lugar || p.direccion].filter(Boolean).join(' · ')}</span>
                                <span className="block text-caption text-muted truncate">{[p.motivo, p.direccion].filter(Boolean).join(' · ')}</span>
                            </span>
                        </li>
                    ))}
                </ol>
                {descartando ? (
                    <label className="block space-y-1.5">
                        <span className="text-label font-black text-muted uppercase tracking-widest px-1">Motivo para la empresa (opcional)</span>
                        <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej.: ese día no hay lugar"
                            className="w-full h-11 px-3 rounded-xl bg-panel text-ink text-body outline-none placeholder:text-muted" />
                    </label>
                ) : (
                    <div className="space-y-1.5">
                        <p className="text-label font-black text-muted uppercase tracking-widest px-1">Técnico</p>
                        <div className="flex flex-wrap gap-1.5">
                            {tecnicos.map(t => (
                                <button key={t.id} type="button" onClick={() => setTecnicoId(t.id)}
                                    className={`h-10 px-3.5 rounded-xl text-label font-black ${t.id === tecnicoId ? 'bg-ink text-page' : 'bg-chip text-secondary'}`}>{t.nombre}</button>
                            ))}
                        </div>
                        <div className="flex items-center justify-between gap-2 pt-2">
                            <p className="text-label font-black text-muted uppercase tracking-widest px-1">¿Cómo vas?</p>
                            <MedioViaje valor={medio} onChange={setMedio} />
                        </div>
                        <p className="text-caption text-muted px-1">Se agenda en este orden, con la hora estimada de cada parada. Al técnico y a la empresa les llega el aviso.</p>
                    </div>
                )}
            </div>
        </ModalShell>
    );
}
